import React, { useEffect, useState } from 'react';
import { Mail, Clock, MapPin, CheckCircle2, AlertCircle } from 'lucide-react';
import SEO from '../components/SEO';
import Container from '../components/ui/Container';
import Button from '../components/ui/Button';
import { sendContactInquiry } from '../services/contactService';
import './Contact.css';

const TOPIC_OPTIONS = [
  { value: '', label: '문의 유형을 선택해주세요' },
  { value: 'product', label: '프로덕트 사용 문의' },
  { value: 'partnership', label: '파트너십 / 협업' },
  { value: 'enterprise', label: '기업 도입 / 제안' },
  { value: 'press', label: '언론 / 인터뷰' },
  { value: 'careers', label: '채용 / 인재 영입' },
  { value: 'etc', label: '기타' },
];

const Contact = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [form, setForm] = useState({
    name: '',
    email: '',
    topic: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);

    try {
      await sendContactInquiry({
        source: 'contact-page',
        name: form.name,
        email: form.email,
        topic: form.topic,
        message: form.message,
      });

      setStatus('success');
      setForm({ name: '', email: '', topic: '', message: '' });
    } catch (err) {
      console.error('Contact form error:', err);
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <SEO
        title="문의하기"
        description="Flowto.AI에 문의하세요. 서비스 도입, 파트너십, 기업 협업, 언론, 채용 문의를 환영합니다."
        url="/contact"
      />

      <header className="contact-hero">
        <Container>
          <span className="contact-hero__eyebrow">Contact</span>
          <h1 className="contact-hero__title">
            무엇이든 물어보세요.
          </h1>
          <p className="contact-hero__sub">
            서비스 도입, 파트너십, 기업 제휴, 언론, 채용 — 어떤 이야기든 환영합니다.
          </p>
        </Container>
      </header>

      <section className="contact-body">
        <Container>
          <div className="contact-layout">
            <aside className="contact-aside">
              <h2 className="contact-aside__title">직접 연락하기</h2>
              <ul className="contact-aside__list">
                <li className="contact-aside__item">
                  <span className="contact-aside__icon"><Mail size={18} /></span>
                  <div>
                    <strong>이메일</strong>
                    <a href="mailto:flowtosupport@gmail.com">flowtosupport@gmail.com</a>
                  </div>
                </li>
                <li className="contact-aside__item">
                  <span className="contact-aside__icon"><Clock size={18} /></span>
                  <div>
                    <strong>응답 시간</strong>
                    <span>평일 영업시간 기준 24시간 이내</span>
                  </div>
                </li>
                <li className="contact-aside__item">
                  <span className="contact-aside__icon"><MapPin size={18} /></span>
                  <div>
                    <strong>위치</strong>
                    <span>Seoul, Republic of Korea</span>
                  </div>
                </li>
              </ul>
            </aside>

            <form className="contact-form" onSubmit={onSubmit} noValidate>
              <div className="contact-form__row">
                <label className="contact-form__field">
                  <span>이름</span>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="홍길동"
                    required
                  />
                </label>
                <label className="contact-form__field">
                  <span>이메일</span>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    required
                  />
                </label>
              </div>

              <label className="contact-form__field">
                <span>문의 유형</span>
                <select
                  name="topic"
                  value={form.topic}
                  onChange={onChange}
                  required
                >
                  {TOPIC_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="contact-form__field">
                <span>문의 내용</span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={7}
                  placeholder="어떤 이야기인지 자유롭게 적어주세요."
                  required
                />
              </label>

              {status === 'success' && (
                <div className="contact-form__alert contact-form__alert--success">
                  <CheckCircle2 size={18} />
                  문의가 전송되었습니다. 빠른 시일 내에 답변드릴게요.
                </div>
              )}
              {status === 'error' && (
                <div className="contact-form__alert contact-form__alert--error">
                  <AlertCircle size={18} />
                  전송에 실패했습니다. 잠시 후 다시 시도하거나, flowtosupport@gmail.com 으로 직접 보내주세요.
                </div>
              )}

              <div className="contact-form__actions">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  pill
                  disabled={submitting}
                >
                  {submitting ? '전송 중…' : '문의 보내기'}
                </Button>
              </div>
            </form>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Contact;
